import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  Boxes,
  Building2,
  FileText,
  FolderTree,
  LayoutDashboard,
  Package,
  Ruler,
  Settings,
  Users,
  Warehouse,
} from 'lucide-react';
import type { Permission } from '@arriba.digital/contracts';

/**
 * Navigation is data, not markup.
 *
 * Each entry declares the permission it needs, and the sidebar filters on
 * `can(permission)`. Hiding a link is a convenience only — the API enforces
 * the same permission, so a hand-typed URL gets a 403 rather than data.
 */

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  permission?: Permission;
  /** Marks the item that should be active for nested routes. */
  matchPrefix?: string;
}

export interface NavSection {
  id: string;
  labelKey?: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    items: [
      {
        to: '/dashboard',
        labelKey: 'nav.dashboard',
        icon: LayoutDashboard,
        matchPrefix: '/dashboard',
      },
      {
        to: '/documents',
        labelKey: 'nav.documents',
        icon: FileText,
        permission: 'document:read',
        matchPrefix: '/documents',
      },
      {
        to: '/stock',
        labelKey: 'nav.stock',
        icon: Boxes,
        permission: 'stock:read',
        matchPrefix: '/stock',
      },
      {
        to: '/stock/movements',
        labelKey: 'nav.movements',
        icon: ArrowLeftRight,
        permission: 'stock:read',
      },
    ],
  },
  {
    id: 'catalog',
    labelKey: 'nav.catalog',
    items: [
      {
        to: '/catalog/products',
        labelKey: 'nav.products',
        icon: Package,
        permission: 'catalog:read',
      },
      {
        to: '/catalog/categories',
        labelKey: 'nav.categories',
        icon: FolderTree,
        permission: 'catalog:read',
      },
      {
        to: '/catalog/units',
        labelKey: 'nav.units',
        icon: Ruler,
        permission: 'catalog:read',
      },
    ],
  },
  {
    id: 'operations',
    labelKey: 'nav.operations',
    items: [
      {
        to: '/warehouses',
        labelKey: 'nav.warehouses',
        icon: Warehouse,
        permission: 'warehouse:read',
      },
      {
        to: '/counterparties',
        labelKey: 'nav.counterparties',
        icon: Building2,
        permission: 'counterparty:read',
      },
    ],
  },
  {
    id: 'administration',
    labelKey: 'nav.administration',
    items: [
      { to: '/users', labelKey: 'nav.users', icon: Users, permission: 'user:read' },
      { to: '/settings', labelKey: 'nav.settings', icon: Settings },
    ],
  },
];

/** Items surfaced in the mobile bottom bar; the rest live behind "More". */
export const MOBILE_PRIMARY: string[] = ['/dashboard', '/documents', '/stock', '/catalog/products'];
